import { BrowserRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';
import { useState, useEffect, Suspense, lazy } from 'react';
import MarketCheckout from './pages/MarketCheckout';
import Home from './pages/Home';
import Shop from './pages/Shop';
import ProductDetail from './pages/ProductDetail';
import About from './pages/About';
import Contact from './pages/Contact';
import SignUp from './pages/SignUp';
import CustomerLogin from './pages/CustomerLogin';
import ForgotPassword from './pages/ForgotPassword';
import CustomerAccount from './pages/CustomerAccount';
import Checkout from './pages/Checkout';
import OrderConfirmation from './pages/OrderConfirmation';
import PublicLayout from './components/PublicLayout';
import { syncQueue } from './services/syncQueue';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import ProtectedRoute from './components/ProtectedRoute';

// Admin-only pages are lazy-loaded so a customer scanning a market QR code
// (or browsing the shop) never downloads the admin bundle - only whoever
// actually navigates into /admin/* or /login pays for it.
const Login = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const ProductList = lazy(() => import('./pages/ProductList'));
const MarketPrint = lazy(() => import('./pages/MarketPrint'));
const MarketSales = lazy(() => import('./pages/MarketSales'));
const BulkImport = lazy(() => import('./pages/BulkImport'));
const StripeSync = lazy(() => import('./pages/StripeSync'));
const SiteContent = lazy(() => import('./pages/SiteContent'));
const Orders = lazy(() => import('./pages/Orders'));

function RouteLoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600" />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <Router>
          <Suspense fallback={<RouteLoadingFallback />}>
            <Routes>
              {/* Public Routes with PublicLayout */}
              <Route element={<PublicLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/product/:id" element={<ProductDetail />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/account" element={<CustomerAccount />} />
              </Route>

              {/* Customer Auth Pages (no layout) */}
              <Route path="/signup" element={<SignUp />} />
              <Route path="/customer-login" element={<CustomerLogin />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />

              {/* Checkout Pages (no layout) */}
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/order-confirmation" element={<OrderConfirmation />} />
              <Route path="/market" element={<MarketCheckout />} />

              {/* Admin Login (no layout) */}
              <Route path="/login" element={<Login />} />
              <Route path="/admin/login" element={<Login />} />

              {/* Admin Routes with Admin Layout */}
              <Route path="/admin/*" element={
                <ProtectedRoute>
                  <AdminApp />
                </ProtectedRoute>
              } />
            </Routes>
          </Suspense>
        </Router>
      </CartProvider>
    </AuthProvider>
  );
}

function AdminApp() {
  const [syncStatus, setSyncStatus] = useState({ isOnline: true, pendingCount: 0 });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, signOut } = useAuth();

  useEffect(() => {
    // Update sync status periodically
    const updateStatus = async () => {
      const status = await syncQueue.getSyncStatus();
      setSyncStatus(status);
    };

    updateStatus();
    const interval = setInterval(updateStatus, 5000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Admin Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <Link to="/admin" className="flex items-center text-xl font-bold text-primary-600">
                💎 Admin Dashboard
              </Link>
              
              {/* Desktop Navigation */}
              <div className="hidden sm:ml-8 sm:flex sm:space-x-4">
                <NavLink to="/admin">Dashboard</NavLink>
                <NavLink to="/admin/products">Products</NavLink>
                <NavLink to="/admin/orders">Orders</NavLink>
                <NavLink to="/admin/site-content">Site Content</NavLink>
                <NavLink to="/admin/stripe-sync">Stripe</NavLink>
                <NavLink to="/admin/import">Import</NavLink>
                <NavLink to="/admin/market/sales">Market Sales</NavLink>
                <NavLink to="/admin/market/print">Print Checklist</NavLink>
              </div>
            </div>

            {/* Status Indicators */}
            <div className="flex items-center space-x-4">
              {/* Back to Shop */}
              <Link
                to="/shop"
                className="text-sm text-gray-600 hover:text-primary-600 hidden sm:inline"
              >
                View Shop
              </Link>

              {/* User Email & Sign Out */}
              <div className="hidden sm:flex items-center space-x-3">
                <span className="text-sm text-gray-600">{user?.email}</span>
                <button
                  onClick={() => signOut()}
                  className="text-sm text-gray-600 hover:text-gray-900 px-3 py-1 rounded-md hover:bg-gray-100 transition-colors"
                >
                  Sign Out
                </button>
              </div>

              {/* Online/Offline Status */}
              <div className="flex items-center space-x-2">
                <div className={`w-2 h-2 rounded-full ${syncStatus.isOnline ? 'bg-green-500' : 'bg-red-500'}`} />
                <span className="text-sm text-gray-600 hidden sm:inline">
                  {syncStatus.isOnline ? 'Online' : 'Offline'}
                </span>
              </div>

              {/* Sync Status */}
              {syncStatus.pendingCount > 0 && (
                <div className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm">
                  {syncStatus.pendingCount} pending
                </div>
              )}

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="sm:hidden p-2"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="sm:hidden pb-4">
              <MobileNavLink to="/admin" onClick={() => setMobileMenuOpen(false)}>
                Dashboard
              </MobileNavLink>
              <MobileNavLink to="/admin/products" onClick={() => setMobileMenuOpen(false)}>
                Products
              </MobileNavLink>
              <MobileNavLink to="/admin/orders" onClick={() => setMobileMenuOpen(false)}>
                Orders
              </MobileNavLink>
              <MobileNavLink to="/admin/site-content" onClick={() => setMobileMenuOpen(false)}>
                Site Content
              </MobileNavLink>
              <MobileNavLink to="/admin/import" onClick={() => setMobileMenuOpen(false)}>
                Import
              </MobileNavLink>
              <MobileNavLink to="/admin/market/sales" onClick={() => setMobileMenuOpen(false)}>
                Market Sales
              </MobileNavLink>
              <MobileNavLink to="/admin/market/print" onClick={() => setMobileMenuOpen(false)}>
                Print Checklist
              </MobileNavLink>
              <MobileNavLink to="/shop" onClick={() => setMobileMenuOpen(false)}>
                View Shop
              </MobileNavLink>
            </div>
          )}
        </nav>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Suspense fallback={<RouteLoadingFallback />}>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/products" element={<ProductList />} />
            <Route path="/orders" element={<Orders />} />
            <Route path="/site-content" element={<SiteContent />} />
            <Route path="/import" element={<BulkImport />} />
            <Route path="/stripe-sync" element={<StripeSync />} />
            <Route path="/market/sales" element={<MarketSales />} />
            <Route path="/market/print" element={<MarketPrint />} />
          </Routes>
        </Suspense>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-12 no-print">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <p className="text-center text-gray-500 text-sm">
            Earring Inventory Tracker © 2026 | Offline-first PWA
          </p>
        </div>
      </footer>
    </div>
  );
}

function NavLink({ to, children }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:text-primary-600 hover:bg-gray-50 rounded-md transition-colors"
    >
      {children}
    </Link>
  );
}

function MobileNavLink({ to, onClick, children }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-primary-600 hover:bg-gray-50 rounded-md"
    >
      {children}
    </Link>
  );
}

export default App;
